import { Button, Group, Stack, Text, Title } from "@mantine/core";
import { modals } from "@mantine/modals";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Content } from "shared/components/global/Content";
import { ScribeIcon } from "shared/components/global/Scribe/ScribeIcon";
import { AppEuds, EmailEuds, TextEuds, VoiceEuds } from "shared/components/settings/euds";
import { GroupsContainer } from "shared/components/settings/groups";
import { LocationsContainer } from "shared/components/settings/locations";
import { languageQueryOptions, ProfileContainer } from "shared/components/settings/profile";
import { TopicsContainer } from "shared/components/settings/TopicsContainer";
import { features } from "shared/graphql/features";
import { groupsQueryOptions } from "shared/graphql/groups";
import { topicsQueryOptions } from "shared/graphql/topics";
import { useUserGroups } from "shared/graphql/users";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useUserId } from "shared/stores/oidc";
import { logger } from "shared/utils/logger";
import { graphql } from "src/gql";
import { queryClient } from "src/queryClient";

const CommunicationMethodsContainer = () => {
    return (
        <>
            <Content paper p="xl">
                <Content.Heading>
                    <Group gap="xs">
                        <Title order={3} id="emails">
                            Emails
                        </Title>
                        <ScribeIcon src="https://scribehow.com/embed/Add_an_Email_to_your_account__3DwnGPtoTJS7AhtkxteQsw" />
                    </Group>
                </Content.Heading>
                <EmailEuds />
            </Content>
            <Content paper p="xl">
                <Content.Heading>
                    <Group gap="xs">
                        <Title order={3} id="texts">
                            Text Numbers
                        </Title>
                        <ScribeIcon src="https://scribehow.com/embed/Add_and_Verify_a_Text_Number__gcvuC__fSF-yDqka6MUmYQ" />
                    </Group>
                </Content.Heading>
                <TextEuds />
            </Content>
            <Content paper p="xl">
                <Content.Heading>
                    <Group gap="xs">
                        <Title order={3} id="voice">
                            Voice Numbers
                        </Title>
                        <ScribeIcon src="https://scribehow.com/embed/Add_Voice_Phone_Number_to_Bryx_Alerts__O35aaGmDSG6jjEzBjZLPSA" />
                    </Group>
                </Content.Heading>
                <VoiceEuds />
            </Content>
            <Content paper p="xl">
                <Content.Heading>
                    <Title order={3} id="devices">
                        Devices
                    </Title>
                    <ScribeIcon src="https://scribehow.com/embed/Download_the_Bryx_Alerts_mobile_app__8BoWSq1hR6qflLHdEtblPA" />
                </Content.Heading>
                <AppEuds />
            </Content>
        </>
    );
};

const Page = () => {
    const nav = useNavigate();
    const hasTopics = features.enabled("HAS_TOPICS");

    const { userId } = useUserId();
    const { groups } = useUserGroups();

    const deleteUser = useGraphqlMutation(
        graphql(`
            mutation SettingsDeleteUser($userId: UUID!) {
                archiveUser(userId: $userId)
            }
        `),
    );

    function openDeleteAccountModal() {
        modals.openConfirmModal({
            title: "Delete account",
            children: "Are you sure you want to delete your account? This action cannot be undone.",
            labels: { confirm: "Delete account", cancel: "Cancel" },
            confirmProps: { color: "red" },
            onCancel: modals.closeAll,
            onConfirm: () => {
                deleteUser
                    .mutateAsync({ userId })
                    .then(() => nav({ to: "/logout" }))
                    .catch(logger.error);
            },
        });
    }

    return (
        <Content>
            <Content paper p="xl">
                <Content.Heading>
                    <Title order={3} id="profile">
                        Profile Information
                    </Title>
                </Content.Heading>
                <ProfileContainer />
            </Content>
            <CommunicationMethodsContainer />
            <Content paper>
                <Content.Heading>
                    <Stack>
                        <Title order={3} id="locations">
                            Locations
                        </Title>
                        <Text fz="sm">Max 5 Addresses</Text>
                    </Stack>
                </Content.Heading>
                <LocationsContainer />
            </Content>
            {hasTopics && (
                <Content paper stack={{ gap: 0 }}>
                    <Content.Heading>
                        <Title order={3} id="topics">
                            Topic Subscriptions
                        </Title>
                    </Content.Heading>
                    <TopicsContainer />
                </Content>
            )}
            {groups.length > 0 && (
                <Content paper>
                    <Content.Heading>
                        <Title order={3} id="groups">
                            Groups
                        </Title>
                    </Content.Heading>
                    <GroupsContainer />
                </Content>
            )}
            <Content paper>
                <Content.Heading id="delete">Delete Account</Content.Heading>
                <Text>
                    Deleting your account is permanent and cannot be undone. All your data, settings, and content will be permanently lost.
                </Text>
                <Button color="#8B0000" onClick={openDeleteAccountModal}>
                    Delete Account
                </Button>
            </Content>
        </Content>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/settings")({
    component: Page,
    loader: () => {
        queryClient.ensureQueryData(languageQueryOptions).catch(logger.error);
        queryClient.ensureQueryData(topicsQueryOptions.rootTopic).catch(logger.error);
        queryClient.ensureQueryData(groupsQueryOptions.rootGroup).catch(logger.error);
    },
});
